import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { LogoHeader } from './Logo';

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? isDark 
            ? 'bg-[#0D0D0D]/95 backdrop-blur-xl border-b border-white/10' 
            : 'bg-[#FAF7F0]/95 backdrop-blur-xl border-b border-[#D4AF37]/20'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="/" 
            data-testid="logo"
            className="transition-transform duration-300 hover:scale-[1.02]"
          >
            <LogoHeader isDark={isDark} />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            <button
              data-testid="nav-services"
              onClick={() => scrollToSection('services')}
              className={`text-sm transition-colors duration-300 tracking-wide ${
                isDark ? 'text-white/80 hover:text-[#D4AF37]' : 'text-[#1A1A1A]/80 hover:text-[#D4AF37]'
              }`}
            >
              {t('nav.services')}
            </button>
            <button
              data-testid="nav-zone"
              onClick={() => scrollToSection('zone')}
              className={`text-sm transition-colors duration-300 tracking-wide ${
                isDark ? 'text-white/80 hover:text-[#D4AF37]' : 'text-[#1A1A1A]/80 hover:text-[#D4AF37]'
              }`}
            >
              {t('nav.zone')}
            </button>
            <button
              data-testid="nav-contact"
              onClick={() => scrollToSection('contact')}
              className={`text-sm transition-colors duration-300 tracking-wide ${
                isDark ? 'text-white/80 hover:text-[#D4AF37]' : 'text-[#1A1A1A]/80 hover:text-[#D4AF37]'
              }`}
            >
              {t('nav.contact')}
            </button>
          </div>

          {/* Theme Toggle, Language Switcher & CTA */}
          <div className="hidden md:flex items-center gap-4">
            {/* Theme Toggle */}
            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              className={`relative w-14 h-8 rounded-full transition-all duration-400 ${
                isDark 
                  ? 'bg-[#2C2C2C] shadow-[inset_0_2px_10px_rgba(0,0,0,0.5),0_0_20px_rgba(212,175,55,0.2)]' 
                  : 'bg-[#F5E6A3]/50 shadow-[inset_0_2px_10px_rgba(255,255,255,0.5),0_0_20px_rgba(212,175,55,0.3)]'
              } backdrop-blur-md border ${isDark ? 'border-[#D4AF37]/30' : 'border-[#D4AF37]/50'}`}
              aria-label="Toggle theme"
            >
              <div
                className={`absolute top-1 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-400 transform ${
                  isDark 
                    ? 'left-1 bg-[#0D0D0D] shadow-[0_0_10px_rgba(212,175,55,0.5)]' 
                    : 'left-7 bg-[#FAF7F0] shadow-[0_0_10px_rgba(212,175,55,0.8)]'
                }`}
              >
                {isDark ? (
                  <Moon className="w-3.5 h-3.5 text-[#D4AF37] animate-pulse" />
                ) : (
                  <Sun className="w-3.5 h-3.5 text-[#D4AF37] animate-spin-slow" />
                )}
              </div>
            </button>

            {/* Language Switcher */}
            <div className={`flex items-center gap-1 rounded-full px-1 py-1 ${
              isDark 
                ? 'border border-white/20 bg-white/5' 
                : 'border border-[#D4AF37]/30 bg-[#D4AF37]/5'
            }`}>
              <button
                data-testid="lang-fr"
                onClick={() => setLanguage('fr')}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-all duration-300 ${
                  language === 'fr'
                    ? 'bg-[#D4AF37] text-[#0A0A0A]'
                    : isDark ? 'text-white/70 hover:text-white' : 'text-[#1A1A1A]/70 hover:text-[#1A1A1A]'
                }`}
              >
                FR
              </button>
              <button
                data-testid="lang-en"
                onClick={() => setLanguage('en')}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-all duration-300 ${
                  language === 'en'
                    ? 'bg-[#D4AF37] text-[#0A0A0A]'
                    : isDark ? 'text-white/70 hover:text-white' : 'text-[#1A1A1A]/70 hover:text-[#1A1A1A]'
                }`}
              >
                EN
              </button>
            </div>

            {/* CTA Button */}
            <button
              data-testid="nav-cta"
              onClick={() => scrollToSection('contact')}
              className="bg-[#D4AF37] text-[#0A0A0A] px-6 py-2.5 text-sm font-medium rounded-full hover:bg-[#F5E6A3] hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-all duration-300"
            >
              {t('nav.cta')}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-3">
            {/* Mobile Theme Toggle */}
            <button
              data-testid="mobile-theme-toggle"
              onClick={toggleTheme}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-400 ${
                isDark 
                  ? 'bg-[#2C2C2C] border border-[#D4AF37]/30' 
                  : 'bg-[#F5E6A3]/50 border border-[#D4AF37]/50'
              }`}
            >
              {isDark ? (
                <Moon className="w-4 h-4 text-[#D4AF37]" />
              ) : (
                <Sun className="w-4 h-4 text-[#D4AF37]" />
              )}
            </button>

            {/* Mobile Language Switcher */}
            <div className={`flex items-center gap-1 rounded-full px-1 py-1 ${
              isDark ? 'border border-white/20' : 'border border-[#D4AF37]/30'
            }`}>
              <button
                data-testid="mobile-lang-fr"
                onClick={() => setLanguage('fr')}
                className={`px-2 py-0.5 text-xs font-medium rounded-full transition-all duration-300 ${
                  language === 'fr'
                    ? 'bg-[#D4AF37] text-[#0A0A0A]'
                    : isDark ? 'text-white/70' : 'text-[#1A1A1A]/70'
                }`}
              >
                FR
              </button>
              <button
                data-testid="mobile-lang-en"
                onClick={() => setLanguage('en')}
                className={`px-2 py-0.5 text-xs font-medium rounded-full transition-all duration-300 ${
                  language === 'en'
                    ? 'bg-[#D4AF37] text-[#0A0A0A]'
                    : isDark ? 'text-white/70' : 'text-[#1A1A1A]/70'
                }`}
              >
                EN
              </button>
            </div>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          data-testid="mobile-menu"
          className={`md:hidden overflow-hidden transition-all duration-500 ${
            isMobileMenuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className={`py-6 space-y-4 border-t ${isDark ? 'border-white/10' : 'border-[#D4AF37]/20'}`}>
            <button
              data-testid="mobile-nav-services"
              onClick={() => scrollToSection('services')}
              className={`block w-full text-left transition-colors duration-300 py-2 ${
                isDark ? 'text-white/80 hover:text-[#D4AF37]' : 'text-[#1A1A1A]/80 hover:text-[#D4AF37]'
              }`}
            >
              {t('nav.services')}
            </button>
            <button
              data-testid="mobile-nav-zone"
              onClick={() => scrollToSection('zone')}
              className={`block w-full text-left transition-colors duration-300 py-2 ${
                isDark ? 'text-white/80 hover:text-[#D4AF37]' : 'text-[#1A1A1A]/80 hover:text-[#D4AF37]'
              }`}
            >
              {t('nav.zone')}
            </button>
            <button
              data-testid="mobile-nav-contact"
              onClick={() => scrollToSection('contact')}
              className={`block w-full text-left transition-colors duration-300 py-2 ${
                isDark ? 'text-white/80 hover:text-[#D4AF37]' : 'text-[#1A1A1A]/80 hover:text-[#D4AF37]'
              }`}
            >
              {t('nav.contact')}
            </button>
            <button
              data-testid="mobile-nav-cta"
              onClick={() => scrollToSection('contact')}
              className="w-full bg-[#D4AF37] text-[#0A0A0A] px-6 py-3 text-sm font-medium rounded-full hover:bg-[#F5E6A3] transition-all duration-300 mt-4"
            >
              {t('nav.cta')}
            </button>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
